import { useCallback, useEffect, useState } from 'react';
import { Copy, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { api } from '../../../lib/api';
import { formatCents } from '../../../lib/billing';
import { usePermission } from '../../rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import PersonCollapsibleSection from './PersonCollapsibleSection';

type Einmalcode = {
  id: number;
  code: string;
  valueCents: number;
  createdAt: string | null;
  eingeloestAm: string | null;
};

const datum = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '—');

/**
 * Die Einmalcodes des Kontos (T-0253).
 *
 * Ein im Anmelde-Assistenten ausgestellter Code war verschwunden, sobald das
 * Fenster zu war — die Gutscheinverwaltung darf das Büro nicht öffnen. Hier
 * steht er mit Wert und Stand; ein noch nicht eingelöster lässt sich entfernen.
 */
export default function EinmalcodesSection({ personId }: Readonly<{ personId: string | number }>) {
  const darf = usePermission(P.anmeldung.einmalcode_ausstellen);
  const [codes, setCodes] = useState<Einmalcode[]>([]);
  const [laeuft, setLaeuft] = useState<number | null>(null);

  const laden = useCallback(async () => {
    try {
      const antwort = await api.get<{ items: Einmalcode[] }>(`/api/personen/${personId}/einmalcodes`);
      setCodes(Array.isArray(antwort?.items) ? antwort.items : []);
    } catch {
      setCodes([]);
    }
  }, [personId]);

  useEffect(() => {
    if (darf) void laden();
  }, [darf, laden]);

  if (!darf || codes.length === 0) return null;

  const entfernen = async (eintrag: Einmalcode) => {
    setLaeuft(eintrag.id);
    try {
      await api.delete(`/api/enrollments/einmalcode/${eintrag.id}`);
      toast.success(`Einmalcode ${eintrag.code} entfernt`);
      await laden();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Der Code konnte nicht entfernt werden');
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <PersonCollapsibleSection
      title="Einmalcodes"
      description="Persönliche Rabattcodes aus dem Anmelde-Assistenten, je einmal einlösbar."
      defaultOpen
    >
      <ul className="divide-y">
        {codes.map((eintrag) => (
          <li key={eintrag.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm tracking-wider">{eintrag.code}</span>
              <span className="text-sm">{formatCents(eintrag.valueCents)}</span>
              {eintrag.eingeloestAm ? (
                <Badge variant="secondary">eingelöst am {datum(eintrag.eingeloestAm)}</Badge>
              ) : (
                <Badge variant="outline">offen seit {datum(eintrag.createdAt)}</Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  void navigator.clipboard?.writeText(eintrag.code);
                  toast.success('Code kopiert');
                }}
              >
                <Copy className="mr-1 h-4 w-4" aria-hidden /> Kopieren
              </Button>
              {eintrag.eingeloestAm ? null : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={laeuft === eintrag.id}
                  onClick={() => void entfernen(eintrag)}
                >
                  <Trash2 className="mr-1 h-4 w-4" aria-hidden /> Entfernen
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </PersonCollapsibleSection>
  );
}
